// The hold-or-press of one control (R1610, R2214): a press that rests
// `holdMs` is a hold, and a shorter press acts at its release. The tab
// bubble of `ui/tabhold.ts` and the VIEW wheel of `ui/quickwheel.ts` run
// on it. The listener takes the press away from the dispatcher of the
// host, and a press that the browser cancels does nothing. A press that
// travels past `TAP_DEFAULTS.slopPx` before the hold ends as a cancel, as
// in the other touch recognisers; after the hold the finger may travel,
// because it drives the wheel (R2256). The pointer is matched by its id
// alone, so the NEGATIVE ids of a real iPhone work.

import { TAP_DEFAULTS } from './touch';

/** What one hold-or-press control does. */
export interface HoldControl<T extends Element> {
  /** The control a pointer press came down on, or null for a press this
   *  control does not take. */
  pick: (ev: PointerEvent) => T | null;
  /** The press rested `holdMs`. */
  hold: (el: T, pointerId: number) => void;
  /** The press ended before `holdMs`, with a release. */
  press: (el: T) => void;
  /** The press ended in any way, before `press`. */
  end?: (el: T) => void;
}

/** Installs one hold-or-press control on `host`, in the capture phase. */
export function installHold<T extends Element>(host: HTMLElement, c: HoldControl<T>, holdMs: number): void {
  host.addEventListener('pointerdown', (ev) => {
    const el = c.pick(ev);
    if (!el) return;
    ev.preventDefault();
    ev.stopPropagation();
    const id = ev.pointerId;
    const x0 = ev.clientX, y0 = ev.clientY;
    let held = false;
    const timer = setTimeout(() => {
      held = true;
      c.hold(el, id);
    }, holdMs);
    const stop = (): void => {
      clearTimeout(timer);
      window.removeEventListener('pointerup', end, true);
      window.removeEventListener('pointercancel', end, true);
      window.removeEventListener('pointermove', move, true);
      c.end?.(el);
    };
    const end = (e: PointerEvent): void => {
      if (e.pointerId !== id) return;
      stop();
      if (!held && e.type === 'pointerup') c.press(el);
    };
    const move = (e: PointerEvent): void => {
      if (e.pointerId !== id || held) return;
      if (Math.hypot(e.clientX - x0, e.clientY - y0) > TAP_DEFAULTS.slopPx) stop();
    };
    window.addEventListener('pointerup', end, true);
    window.addEventListener('pointercancel', end, true);
    window.addEventListener('pointermove', move, true);
  }, true);
}
